import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getSuggestions } from '../actions/suggest';
import { SUGGEST } from '../config/searchRegistry.generated';

/**
 * Debounced typeahead suggestions for a partially-typed query. (item 3)
 *
 * Returns `{ items, loading, term }`, where `term` is the query the CURRENT
 * items actually describe. That distinction is what lets the panel avoid its
 * most annoying failure: showing "No matches" for a prefix whose request has
 * not landed yet.
 *
 * `minChars` and `debounceMs` come from search-registry.json, the same source
 * the endpoint reads, so the client cannot ask for something the server will
 * refuse to answer.
 */
export default function useSuggest(term, { scope, enabled = true } = {}) {
  const dispatch = useDispatch();
  const state = useSelector((s) => s.suggest);
  const trimmed = (term || '').trim();
  const active = enabled && trimmed.length >= SUGGEST.minChars;

  React.useEffect(() => {
    if (!active) return undefined;
    const timer = setTimeout(
      () => dispatch(getSuggestions(trimmed, { scope })),
      SUGGEST.debounceMs,
    );
    // Cancels the request that was never sent. A keystroke inside the debounce
    // window replaces the pending timer rather than queueing a second fetch,
    // so a fast typist issues one request at the end of a burst, not one per
    // character.
    return () => clearTimeout(timer);
  }, [dispatch, trimmed, scope, active]);

  return React.useMemo(
    () => ({
      items: active ? state?.items || [] : [],
      loading: !!state?.loading,
      // Empty until a response for THIS term has landed, so the panel can
      // stay quiet rather than claiming there are no matches.
      term: state?.term || '',
    }),
    [active, state?.items, state?.loading, state?.term],
  );
}
